import type { PaginationState } from "@/shared/hooks";
import { mapSortingToRequestDto } from "@/shared/utils/data-table";
import { Currency } from "@/shared/value-objects/currency.vo";
import { useGetControlResultListPaginated } from "@/spz-api/control-results/control-results";
import type {
  ControlResultColumnResponseDto,
  ControlResultListItemResponseDto,
} from "@/spz-api/model";
import { useCallback, useMemo } from "react";
import { useControlResultsColumns } from "../hooks/use-control-results-columns";
import { useControlResultFiltersStore } from "../store/control-result-filters.store";
import { buildApiFilters } from "../utils/filter-parser/filter-parser";

export const useControlResultListPaginated = ({
  controlId,
  paginationParams,
  controlResultColumns,
}: {
  controlId: string;
  companyId: string;
  paginationParams: PaginationState["paginationParams"];
  controlResultColumns: ControlResultColumnResponseDto[];
}) => {
  // Example usage of filters with the useGetControlResultListPaginated hook:
  //
  // - To filter control results, build an array of filter objects like:
  //     { column: "number_of_the_import", operator: "gt", value: 0 }
  //     { column: "document_date", operator: "between", value: ["2025-01-01", "2025-12-31"] }
  // - Wrap the array in an object: { filters }
  // - Serialize to JSON: JSON.stringify({ filters })
  // - Pass as the "filters" parameter to the hook
  //
  // Example:
  //   const filters = [
  //     { column: "number_of_the_import", operator: "gt", value: 0 },
  //     { column: "document_date", operator: "between", value: ["2025-01-01", "2025-12-31"] },
  //   ];
  //   const filtersJson = JSON.stringify({ filters });
  //
  //   const { data, error, isLoading } = useGetControlResultListPaginated(controlId, {
  //     ...paginationParams,
  //     filters: filtersJson,
  //   });
  //
  //   // If no filters are needed, you can omit the "filters" parameter or pass an empty string.

  const { columnFilters, sorting } = useControlResultFiltersStore();
  const columnTypes = Object.fromEntries(
    controlResultColumns.map((col) => [col.columnKey, col.type.toLowerCase()]),
  );

  const filtersJson = buildApiFilters(columnFilters, columnTypes);
  // Only one sort can be enabled on this table
  const firstSort = mapSortingToRequestDto(sorting)[0];

  const {
    data: controlResults,
    error,
    isLoading,
  } = useGetControlResultListPaginated(controlId, {
    ...paginationParams,
    filters: filtersJson,
    sortField: firstSort?.sortField,
    sortDirection: firstSort?.sortDirection,
  });

  const controlResultsWithRelationships = useMemo(() => {
    if (!controlResults) return [];
    return controlResults.data.map((item) => ({
      ...item,
      owner: controlResults.relationships.owners.find(
        (owner) => owner.id === item.ownerId,
      ),
      currentWorkflowStep: controlResults.relationships.workflowSteps.find(
        (step) => step.id === item.currentWorkflowStepId,
      ),
      associatedCurrency: controlResults.relationships.associatedCurrency,
      priority: controlResults.relationships.priorities.find(
        (priority) => priority.id === item.priorityId,
      ),
    }));
  }, [controlResults]);

  const columns = useControlResultsColumns({
    controlResultColumns,
  });

  const totalPages = controlResults?.pageCount ?? 1;

  const totalCount = controlResults?.total ?? 0;

  const dataTableData: ControlResultListItemResponseDto[] =
    controlResultsWithRelationships;
  const getRowById = useCallback(
    (id: string) => {
      return dataTableData.find((item) => item.id === id);
    },
    [dataTableData],
  );

  const currency = controlResults?.relationships.associatedCurrency
    ? Currency.create(controlResults.relationships.associatedCurrency)
    : undefined;

  return {
    error,
    loading: isLoading,
    loaded: !isLoading,
    dataTableData,
    columns,
    totalPages,
    totalCount,
    currency,
    getRowById,
    controlResults,
  };
};
